import { useQuery } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { feedbackApi, type FeedbackEntry } from "@/api/feedback.ts";

function npsColor(score: number): "success" | "warning" | "danger" {
  if (score >= 9) return "success";
  if (score >= 7) return "warning";
  return "danger";
}

export function AdminFeedbackPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "feedback"],
    queryFn: () => feedbackApi.adminList({ limit: 100 }),
  });

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const entries: FeedbackEntry[] = data?.entries ?? [];
  const stats = data?.stats;

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center gap-2 mb-6">
        <MessageSquare className="w-5 h-5 text-surface-400" />
        <h1 className="text-xl font-semibold text-surface-900">NPS Feedback</h1>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-3 mb-6">
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold text-surface-900">{stats.nps}</div>
              <div className="text-xs text-surface-500 mt-1">NPS Score</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold text-surface-900">{stats.avgScore}</div>
              <div className="text-xs text-surface-500 mt-1">Avg Score</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">{stats.promoters}</div>
              <div className="text-xs text-surface-500 mt-1">Promoters</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold text-amber-600">{stats.passives}</div>
              <div className="text-xs text-surface-500 mt-1">Passives</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.detractors}</div>
              <div className="text-xs text-surface-500 mt-1">Detractors</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Entries table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">All Responses ({data?.pagination?.total ?? 0})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {entries.length === 0 ? (
            <div className="py-8 text-center text-surface-400 text-sm">No feedback yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-100">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-surface-500">User</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-surface-500">Score</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-surface-500">Comment</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-surface-500">Trigger</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-surface-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-b border-surface-50 hover:bg-surface-25">
                    <td className="px-4 py-2.5 font-mono text-xs text-surface-600">{e.userEmail}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge variant={npsColor(e.score)}>{e.score}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-surface-700 max-w-[300px] truncate">{e.comment || "—"}</td>
                    <td className="px-4 py-2.5"><Badge variant="default">{e.trigger}</Badge></td>
                    <td className="px-4 py-2.5 text-surface-500 text-xs">{new Date(e.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
